<script setup lang="ts">
import { faRepeatAlt } from '@fortawesome/pro-regular-svg-icons';

const { hideHeader, swapDisabled, swapLoading, isChangeSearch, loading } =
    defineProps<IProps>();
const emit = defineEmits<{
    swap: [];
    forceReload: [];
    submit: [];
}>();
interface IProps {
    swapDisabled?: boolean;
    hideHeader?: boolean;
    isChangeSearch?: boolean;
    swapLoading?: boolean;
    loading?: boolean;
}
const swapped = ref(false);
const swap = () => {
    swapped.value = !swapped.value;
    emit('swap');
};

const isMounted = useMounted();
const hasYalda = computed(() => false);
</script>

<template>
    <form class="search-form-wrapper" @submit.prevent>
        <div v-show="!hideHeader" class="form-header">
            <slot name="header" />
        </div>
        <div class="inputs-container">
            <div class="location-picker-container">
                <div v-if="!!$slots['origin']" class="origin-picker-container">
                    <slot name="origin" />
                </div>
                <mr-button
                    v-if="!!$slots['origin']"
                    aria-label="تغییر مسیر سفر"
                    class="swap-button"
                    :icon-button="faRepeatAlt"
                    variant="transparent"
                    :class="{
                        swapped,
                        'swap-loading': swapLoading,
                    }"
                    :disabled="isMounted && swapDisabled"
                    size="medium"
                    @click="swap"
                />
                <div
                    class="destination-picker-container"
                    :class="{ 'have-origin': !!$slots['origin'] }"
                >
                    <slot name="destination" />
                </div>
            </div>
            <div
                class="date-picker-container"
                :class="{ 'have-pax-picker': !!$slots['pax-picker'] }"
            >
                <slot name="date-picker" />
            </div>
            <div v-if="!!$slots['pax-picker']" class="pax-picker-container">
                <slot name="pax-picker" />
            </div>
            <div class="submit-container">
                <slot
                    name="submit-button"
                    :disabled="isChangeSearch && loading"
                    :on-click="() => emit('submit')"
                    title="جستجو"
                >
                    <mr-button
                        full-width
                        :disabled="isChangeSearch && loading"
                        class="yalda"
                        @click="() => emit('submit')"
                    >
                        جستجو
                        <ClientOnly>
                            <slot
                                name="watermelon"
                                :classname="[
                                    'icon',
                                    { 'yalda-icon': hasYalda },
                                ]"
                            />
                        </ClientOnly>
                    </mr-button>
                </slot>
            </div>
            <slot />
        </div>
    </form>
</template>

<style lang="scss" scoped>
@keyframes bounce {
    0% {
        opacity: 0;
        transform: translate3d(150px, 0, 0);
    }
    50% {
        opacity: 1;
    }
    100% {
        transform: translate3d(0, 0, 0);
    }
}
@keyframes flip {
    0% {
        transform: scaleX(1);
    }
    100% {
        transform: scaleX(-1);
    }
}
.yalda {
    position: relative;
    overflow: visible;
    :slotted(.icon) {
        opacity: 0;
        position: absolute;
        width: 70px;
        height: 70px;
        top: -67px;
        left: 0;
        pointer-events: none;
        @include mobile-only {
            top: -20px;
        }
    }
    :slotted(.yalda-icon) {
        opacity: 1;
        transform: scaleX(-1);
        animation:
            bounce 1s ease-in-out,
            flip 200ms ease-in-out 1s;
    }
}

.search-form-wrapper {
    @include shadow(1);
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: $app-container-max-width;
    background: white;
    border-radius: 14px;
    padding: 24px;
}

.form-header {
    display: flex;
    align-items: center;
    margin-bottom: 24px;
}

.inputs-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.location-picker-container {
    flex: 2;
    display: flex;
    align-items: center;
    margin-left: 8px;
    border-radius: 8px;
    max-width: 376px;
    position: relative;
    @include f-gap(12px);
    .origin-picker-container {
        flex: 1 1 50%;
    }
    .swap-button {
        flex: 1 0 52px;
        position: absolute;
        right: 50%;
        transform: translateX(calc(50% + 6px));
        z-index: 1;
        :deep() {
            .icon {
                transition: transform 200ms linear;
                transform: rotate(0deg);
            }
        }
        &.swapped :deep() {
            .icon {
                transform: rotate(180deg);
            }
        }
        &.swap-loading :deep() {
            .icon {
                animation: swap-loading infinite 500ms;
            }
        }
        @keyframes swap-loading {
            from {
                transform: rotate(270deg);
            }
            to {
                transform: rotate(0deg);
            }
        }
    }
    .destination-picker-container {
        flex: 1 1 50%;
        &.have-origin :deep() {
            .mr-inputs-container .mr-icon-container {
                display: none;
            }
        }
    }
}

.date-picker-container {
    flex: 3;
    margin-left: 8px;
    &.have-pax-picker {
        flex: 2;
    }
}

.pax-picker-container {
    flex: 1;
    margin-left: 8px;
}

.submit-container {
    flex: 1;
}

@media (max-width: $app-mobile-max-width) {
    .search-form-wrapper {
        padding: 0;
        border-radius: 0;
    }

    .form-header {
        @include no-scrollbar;
        margin: 12px;
        height: 48px;
        justify-content: space-between;
    }

    .inputs-container {
        flex-direction: column;
        padding: 0 20px 20px 20px;
    }

    .location-picker-container {
        flex-wrap: wrap;
        margin-left: 0;
        margin-bottom: 8px;
        width: 100%;
        max-width: unset;
        @include f-gap(0);
        .origin-picker-container {
            width: calc(100% - 55px);
            flex-basis: calc(100% - 55px);
            margin-bottom: 12px;
        }
        .swap-button {
            flex: unset;
            left: 8px;
            right: auto;
            transform: none;
            :deep() {
                .icon {
                    transition: transform 200ms linear;
                    transform: rotate(90deg);
                }
            }
            &.swapped :deep() {
                .icon {
                    transform: rotate(270deg);
                }
            }
        }
        .destination-picker-container {
            flex-grow: 1;
            width: 100%;
            flex-basis: 100%;
            &.have-origin :deep() {
                .mr-inputs-container .input-wrapper {
                    border-top-right-radius: 8px !important;
                    border-bottom-right-radius: 8px !important;
                }
                .mr-icon-container {
                    display: flex !important;
                }
            }
        }
    }

    .date-picker-container {
        flex: unset;
        margin-left: 0;
        margin-bottom: 12px;
        width: 100%;
    }

    .pax-picker-container {
        width: 100%;
        flex: unset;
        margin-left: 0;
        margin-bottom: 12px;
    }

    .submit-container {
        width: 100%;
        flex: unset;
    }
}
</style>
